/**
 * Appointment API - provider-side scheduling.
 */
import apiClient, { apiGet, apiGetList, apiPost, apiPut, apiDelete } from "./client";
import type { Appointment, PaginatedResponse } from "@/types";
import { clinicTimezone } from "@/lib/clinic-time";

// ── Extended types for booking ────────────────────────────────────────────────

export interface AvailableSlot {
  start_time: string;
  end_time: string;
  duration: number;
}

export interface EligibilityResponse {
  eligible: boolean;
  plan_name: string;
  policy_number: string;
  group_number?: string;
  copay?: number;
  deductible?: number;
  deductible_met?: number;
  out_of_pocket_max?: number;
  out_of_pocket_met?: number;
  coverage_active: boolean;
  effective_date?: string;
  termination_date?: string;
  message?: string;
}

export interface PractitionerSummary {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  specialty?: string;
  avatar_url?: string;
}

export interface FacilitySummary {
  id: string;
  name: string;
  address?: string;
}

export interface BookAppointmentPayload {
  organization_id?: string;
  patient_id: string;
  practitioner_id: string;
  facility_id?: string;
  title?: string;
  appointment_type: "office-visit" | "telehealth" | "follow-up" | "new-patient" | "physical" | "urgent" | "procedure" | "group";
  start_time: string;
  duration: number;
  reason_code?: string;
  reason_display?: string;
  chief_complaint?: string;
  triage_priority?: "routine" | "priority" | "urgent";
  reminder_channel?: "sms" | "email" | "both" | "none";
  room?: string;
  notes?: string;
  recurrence_rule?: string;
  telehealth_enabled?: boolean;
  telehealth_platform?: "zoom" | "doxy" | "native" | "webex";
  additional_staff_ids?: string[];
  intake_questionnaire_ids?: string[];
  symptoms?: string[];
}

export interface CreateBookingEscalationPayload {
  patient_id: string;
  practitioner_id: string;
  facility_id?: string;
  request_type: "waitlist" | "overbook";
  appointment_type: "office-visit" | "telehealth" | "follow-up" | "new-patient" | "physical" | "urgent" | "procedure" | "group";
  visit_reason_code?: string;
  visit_reason_label?: string;
  preferred_date?: string;
  duration: number;
  notes?: string;
}

export interface BookingEscalation {
  id: string;
  organization_id: string;
  patient_id: string;
  practitioner_id: string;
  facility_id?: string;
  request_type: "waitlist" | "overbook";
  appointment_type: string;
  visit_reason_code?: string;
  visit_reason_label?: string;
  preferred_date?: string;
  duration: number;
  notes?: string;
  status: "pending" | "resolved" | "dismissed";
  created_by?: string;
  created_at: string;
  updated_at: string;
}


/** List appointments with optional filters. */
export function listAppointments(params?: {
  provider_id?: string;
  patient_id?: string;
  status?: string;
  appointment_type?: string;
  date_from?: string;
  date_to?: string;
  /** IANA zone the date_from/date_to days are in (the clinic's). */
  tz?: string;
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<PaginatedResponse<Appointment>> {
  // Date ranges are clinic days, not this computer's or UTC days.
  const withTz = params && (params.date_from || params.date_to) && !params.tz ? { ...params, tz: clinicTimezone() } : params;
  return apiGetList<Appointment>("/appointments", { params: withTz });
}

/** Largest page the server returns for appointment lists. */
const APPOINTMENT_PAGE_MAX = 100;

/**
 * List every appointment matching the filters, following pages. Calendars and
 * dashboards need the whole range - a single page silently drops the rest.
 */
export async function listAllAppointments(
  params?: Omit<Parameters<typeof listAppointments>[0], "page" | "per_page">,
): Promise<PaginatedResponse<Appointment>> {
  const first = await listAppointments({ ...params, page: 1, per_page: APPOINTMENT_PAGE_MAX });
  const totalPages = Math.min(first.meta?.total_pages ?? 1, 50);
  if (totalPages <= 1) return first;
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      listAppointments({ ...params, page: i + 2, per_page: APPOINTMENT_PAGE_MAX }),
    ),
  );
  const data = [first, ...rest].flatMap((r) => r.data ?? []);
  return { data, meta: { ...first.meta, page: 1, per_page: data.length, total_pages: 1 } };
}

/** Get a single appointment by ID. */
export function getAppointment(id: string): Promise<Appointment> {
  return apiGet<Appointment>(`/appointments/${id}`);
}

/** Create a new appointment. */
export function createAppointment(
  data: Partial<Appointment>,
): Promise<Appointment> {
  return apiPost<Appointment>("/appointments", data);
}

/** Update an existing appointment. */
export function updateAppointment(
  id: string,
  data: Partial<Appointment>,
): Promise<Appointment> {
  return apiPut<Appointment>(`/appointments/${id}`, data);
}

export interface SeriesConflict {
  appointment_id: string;
  proposed_start: string;
  reason: string;
}

export interface SeriesUpdateResult {
  updated: number;
  skipped: number;
  conflicts: SeriesConflict[];
}

/**
 * Apply a change to this visit and every later booked visit in its series.
 * Resolves with `conflicts` (and nothing saved) when some visits clash and
 * skipConflicts is false.
 */
export async function updateAppointmentSeries(
  id: string,
  data: Partial<Appointment> & { notify_patient?: boolean },
  skipConflicts = false,
): Promise<SeriesUpdateResult> {
  const res = await apiClient.put(`/appointments/${id}/series`, { ...data, skip_conflicts: skipConflicts }, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
  });
  return (res.data?.data ?? res.data) as SeriesUpdateResult;
}

/** Cancel an appointment (requires reason). */
export function cancelAppointment(id: string, reason = "Cancelled by staff"): Promise<Appointment> {
  return apiPost<Appointment>(`/appointments/${id}/cancel`, { reason });
}

/** Cancel this visit and every later visit in its repeating series. */
export function cancelAppointmentSeries(
  id: string,
  reason = "Cancelled by staff",
): Promise<{ cancelled: number; kept: number }> {
  return apiPost<{ cancelled: number; kept: number }>(`/appointments/${id}/cancel-series`, { reason });
}

/** Permanently remove a cancelled appointment from the schedule. */
export function deleteAppointment(id: string): Promise<void> {
  return apiDelete<void>(`/appointments/${id}`);
}

/** Check in a patient for an appointment. */
export function checkInPatient(id: string): Promise<Appointment> {
  return apiPost<Appointment>(`/appointments/${id}/check-in`);
}

/** Mark an appointment as no-show. */
export function markAppointmentNoShow(
  id: string,
  reason = "Marked no-show from patient tracker",
): Promise<Appointment> {
  return apiPost<Appointment>(`/appointments/${id}/no-show`, { reason });
}

/** Get today's schedule, optionally filtered by provider. */
export function getTodaySchedule(providerId?: string): Promise<Appointment[]> {
  return apiGet<Appointment[]>("/appointments/today", {
    params: providerId ? { provider_id: providerId } : undefined,
  });
}

/** Count proposed/pending appointments for the current provider. */
export function getTentativeCount(providerId?: string): Promise<number> {
  return apiGet<{ tentative: number }>("/appointments/tentative-count", {
    params: providerId ? { provider_id: providerId } : undefined,
  }).then((r) => r.tentative);
}

// ── Booking APIs ──────────────────────────────────────────────────────────────

/** Get available slots for a provider on a specific date (YYYY-MM-DD). */
export function listAvailableSlots(params: {
  provider_id: string;
  facility_id?: string;
  date: string;
  duration?: number;
}): Promise<AvailableSlot[]> {
  return apiGet<AvailableSlot[]>("/appointments/available-slots", { params });
}

/** List practitioners in the organization. */
export function listPractitioners(params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<{ data: PractitionerSummary[] }> {
  return apiGet<{ data: PractitionerSummary[] }>("/practitioners", { params });
}

/** List facilities/locations in the organization. */
export async function listFacilities(organizationId: string): Promise<FacilitySummary[]> {
  const res = await apiGetList<FacilitySummary>(
    `/organizations/${organizationId}/facilities`,
    { params: { per_page: 200 } },
  );
  return (res.data ?? []).map((f) => ({
    id: f.id,
    name: f.name,
    address: f.address,
  }));
}

/** Appointment category (e.g. "Primary Care", "Cardiology") */
export interface AppointmentCategorySummary {
  id:               string;
  name:             string;
  default_duration: number;
  color:            string;
}

/** List appointment categories for the organization. */
export function listAppointmentCategories(): Promise<AppointmentCategorySummary[]> {
  return apiGet<AppointmentCategorySummary[]>("/appointment-categories");
}

export interface CreateAppointmentCategoryRequest {
  name: string;
  description?: string;
  default_duration: number;
  color?: string;
}

export function createAppointmentCategory(
  req: CreateAppointmentCategoryRequest,
): Promise<AppointmentCategorySummary> {
  return apiPost<AppointmentCategorySummary>("/appointment-categories", req);
}

export interface UpdateAppointmentCategoryRequest {
  name?: string;
  description?: string;
  default_duration?: number;
  color?: string;
  active?: boolean;
}

export function updateAppointmentCategory(
  id: string,
  req: UpdateAppointmentCategoryRequest,
): Promise<AppointmentCategorySummary> {
  return apiPut<AppointmentCategorySummary>(`/appointment-categories/${id}`, req);
}

/** Check insurance eligibility for a patient on a given date. */
export function checkInsuranceEligibility(
  patientId: string,
  appointmentDate: string,
): Promise<EligibilityResponse> {
  return apiPost<EligibilityResponse>("/insurance/check-eligibility", {
    patient_id: patientId,
    appointment_date: appointmentDate,
  });
}

/** Book a full appointment (with questionnaires + symptoms). */
export function bookAppointment(
  data: BookAppointmentPayload,
): Promise<Appointment> {
  return apiPost<Appointment>("/appointments", data);
}

export function createBookingEscalation(
  data: CreateBookingEscalationPayload,
): Promise<BookingEscalation> {
  return apiPost<BookingEscalation>("/appointments/escalations", data);
}

export function listBookingEscalations(params?: {
  status?: string;
  request_type?: "waitlist" | "overbook";
  page?: number;
  per_page?: number;
}): Promise<PaginatedResponse<BookingEscalation>> {
  return apiGetList<BookingEscalation>("/appointments/escalations", { params });
}
